import { FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { isNotEmpty } from '../../../../../_dashboard/helpers'
import { Option } from '../core/_models'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { DataLoading } from '../../../../../_dashboard/partials/components/loading/DataLoading'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { createOne, getAll, getOneByIdbylang, updateOne } from '../../../../services/common_requests'
import { useIntl } from 'react-intl'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

type Props = {
  isOptionLoading: boolean
  Option: Option
}

const editOptionSchema = Yup.object().shape({
  translations: Yup.array().of(
    Yup.object().shape({
      title: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Title is required'),
    })
  ),
})

const OptionEditModalForm: FC<Props> = ({ Option, isOptionLoading }) => {
  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const { refetchData } = useQueryResponse()
  const { state } = useQueryRequest();
  const [query, setQuery] = useState<any>(state);
  const intl = useIntl()
  const [OptionForEdit] = useState<Option>({
    ...Option,
    translations: Option.translations ?? [{ language: 'en', name: '' }]
  })
  const LANGUAGE_NAMES: { [key: string]: string } = {
    en: intl.formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
    fr: intl.formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
    es: intl.formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
    ar: intl.formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
  };

  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  useEffect(() => {
    if (languages.length > 0) {
      setSelectedLanguage(languages[0].code);
    }
  }, [languages]);
  useEffect(() => {
    getAll(query, '/languages').then((data) => {
      if (data && Array.isArray(data)) {
        const formattedLanguages = data.map((code: string) => ({
          code,
          name: LANGUAGE_NAMES[code] || code.toUpperCase(),
        }));
        setLanguages(formattedLanguages);
      }
    });
  }, [query]);

  useEffect(() => {
    if (itemIdForUpdate && selectedLanguage) {
      getOneByIdbylang(`/options/${itemIdForUpdate}?language=${selectedLanguage}`)
        .then((data) => {
          if (data && data.translations) {
            // Mettre à jour toutes les traductions
            formik.setValues({
              ...formik.values,
              translations: data.translations.map((t: any) => ({
                ...t,
                _id: t._id
              }))
            });
          }
        })
        .catch((error) => console.error('Error fetching translation:', error));
    }
  }, [selectedLanguage, itemIdForUpdate]);






  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({

    initialValues: OptionForEdit,
    validationSchema: editOptionSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      if (isNotEmpty(itemIdForUpdate)) {
        const selectedTranslation = values.translations.find(t => t.language === selectedLanguage);

        if (!selectedTranslation || !selectedTranslation._id) {
          toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND' }));
          return;
        }
        const payload = {
          title: selectedTranslation.title,
        };

        await updateOne(payload, `/options/translation/${selectedTranslation._id}`).then((response) => {
          if (response?.payload?.code === 2021) {
            toast.success(
              intl.formatMessage({
                id: 'ADMIN.COMMON.SUCCESS.OPTION_UPDATED_SUCCESSFULLY',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
            cancel(true)
            refetchData()
          }
          else if (response?.payload?.code === 4401) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ID_NOT_EXIST',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
          else if (response?.payload?.code === 9100) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
          else {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
        })

      }
      else {
        const payload = {
          title: values.translations[0].title,
          translations: values.translations.map(translation => ({
            language: translation.language || 'en',
            title: translation.title,
          })),
        };
        try {

          await createOne(payload, '/options').then((response) => {
            if (response?.payload?.code === 2021) {

              toast.success(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.SUCCESS.OPTION_ADDED_SUCCESSFULLY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
              cancel(true)
            }
            else if (response?.payload?.code === 4400) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
            }
            else {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
            }
          })
        }
        catch (ex) {
          console.error(ex)
        } finally {
          setSubmitting(true)
          cancel(true)
        }
      }
    },
  })
  const selectedTranslation = formik.values.translations.find(t => t.language === selectedLanguage);

  return (
    <>
      <form
        id='kt_modal_add_option_form'
        className='form'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n15 pe-6'
          id='kt_modal_add_option_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_option_header'
          data-kt-scroll-wrappers='#kt_modal_add_Option_scroll'
          data-kt-scroll-offset='300px'
        >
          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
            {formik.values.translations.map((translation, index) => (
              <div key={index}>
                <select
                  id={`translations[${index}].language`}
                  name={`translations[${index}].language`}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setSelectedLanguage(e.target.value); // Met à jour la langue sélectionnée
                  }}
                  value={translation.language}

                  className='form-select'
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {formik.errors.translations && (formik.errors.translations[index] as { language?: string })?.language && (
                  <div>{(formik.errors.translations[index] as { language: string }).language}</div>
                )}
              </div>
            ))}
          </div>
          {/* begin::Input group */}
          <div className='fv-row mb-4'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.NAME' })}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder={intl.formatMessage({ id: 'COLUMNS.NAME' })}
              value={selectedTranslation?.title || ''}
              onChange={(e) => {
                const index = formik.values.translations.findIndex(t => t.language === selectedLanguage);
                if (index !== -1) {
                  formik.setFieldValue(`translations[${index}].title`, e.target.value);
                }
              }}
              type="text"
              name="title"
              className={clsx(
                'form-control mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.translations?.[0]?.title && (formik.errors.translations?.[0] as { title?: string })?.title },
                { 'is-valid': formik.touched.translations?.[0]?.title && !(formik.errors.translations?.[0] as { title?: string })?.title }
              )}
              autoComplete="off"
              disabled={formik.isSubmitting || isOptionLoading}
            />


            {/* end::Input */}
          </div>
          {/* end::Input group */}
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-options-modal-action='cancel'
            disabled={formik.isSubmitting || isOptionLoading}
          >
            {intl.formatMessage({ id: 'HEADER.RESET' })}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-options-modal-action='submit'
            disabled={
              isOptionLoading || formik.isSubmitting || !formik.isValid || !formik.touched
            }
          >
            <span className='indicator-label'>           {intl.formatMessage({ id: 'HEADER.SAVE' })}
            </span>
            {(formik.isSubmitting || isOptionLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isOptionLoading) && <DataLoading />}
    </>
  )
}

export { OptionEditModalForm }
